<template>
    <div class="oauth-callback">
        <div class="oauth-callback__panel">
            <h1 class="oauth-callback__title">{{ title }}</h1>
            <p class="oauth-callback__body">{{ message }}</p>
            <button v-if="showClose" type="button" class="btn btn-secondary btn-sm" @click="closeSelf">
                Close
            </button>
        </div>
    </div>
</template>

<script>
import { MCP_OAUTH_ACK, MCP_OAUTH_MESSAGE, openOAuthChannel } from '../../utils/mcpOauthPopup'

/** How long to wait for the settings page to acknowledge before asking the user to close. */
const ACK_TIMEOUT_MS = 2000

/**
 * Landing page for the MCP OAuth redirect URI.
 *
 * Runs inside the consent popup, which the authorization server has just sent
 * back to our origin. Its entire job is to hand the query parameters to the
 * window that started the connect and get out of the way — it deliberately does
 * **not** call the agents service itself. The waiting window holds the session
 * and the store; duplicating the exchange here would mean a second place that
 * has to get the `iss` forwarding right.
 *
 * The result goes out over a same-origin BroadcastChannel, and to
 * `window.opener` too when there still is one. The opener alone is not enough:
 * an authorization server that sends `Cross-Origin-Opener-Policy: same-origin`
 * (Hugging Face, among others) severs it for good — see `utils/mcpOauthPopup`.
 * The waiting window acknowledges on the channel; only then does this page
 * close itself, so a missed hand-off is never shown as a success.
 *
 * Registered as a top-level route rather than under the portal shell: it draws
 * in a 680px popup for well under a second, and there is nothing to navigate.
 *
 * `iss` is forwarded verbatim when present. The service compares it against the
 * issuer recorded at discovery time *before* redeeming the code (RFC 9207).
 * Dropping it here would silently disable the check that closes
 * authorization-server mix-up attacks — which is why it is passed through
 * untouched rather than parsed, normalized, or validated in the browser.
 */
export default {
    name: 'PaiceOAuthCallback',
    data() {
        return {
            title: 'Finishing up',
            message: 'Completing the connection…',
            showClose: false,
        }
    },
    mounted() {
        const params = new URLSearchParams(window.location.search)
        const payload = {
            type: MCP_OAUTH_MESSAGE,
            code: params.get('code'),
            state: params.get('state'),
            // Verbatim. Never normalized here — see the class comment.
            iss: params.get('iss'),
            error: params.get('error'),
            errorDescription: params.get('error_description'),
        }

        if (!payload.error && !payload.code) {
            payload.error = 'invalid_response'
            payload.errorDescription =
                'The authorization server did not return an authorization code.'
        }

        this.report(payload)
    },
    beforeUnmount() {
        this.teardown()
    },
    methods: {
        report(payload) {
            this.channel = openOAuthChannel()
            if (this.channel) {
                this.channel.onmessage = (event) => {
                    const data = event.data
                    if (data && data.type === MCP_OAUTH_ACK && data.state === payload.state) {
                        this.onAcknowledged(payload)
                    }
                }
                try { this.channel.postMessage(payload) } catch (e) { /* closed */ }
            }
            const delivered = this.postToOpener(payload)
            if (!this.channel && delivered) {
                // No channel means no ack can come back; the opener path is
                // all there is, and it went through.
                this.onAcknowledged(payload)
                return
            }

            this.ackTimer = setTimeout(() => this.onUnacknowledged(payload), ACK_TIMEOUT_MS)
        },

        onAcknowledged(payload) {
            this.teardown()
            if (payload.error) {
                this.title = 'Connection failed'
                this.message = payload.errorDescription || payload.error
                this.showClose = true
                return
            }
            // Handed off cleanly. Close on the next tick so the ack round-trip
            // is fully done before the window goes away.
            setTimeout(() => this.closeSelf(), 150)
        },

        onUnacknowledged(payload) {
            // Nobody took it: the settings page was closed or cancelled, or the
            // user landed here directly. Do not claim success.
            this.teardown()
            this.showClose = true
            if (payload.error) {
                this.title = 'Connection failed'
                this.message = payload.errorDescription || payload.error
                return
            }
            this.title = 'Connection not completed'
            this.message = 'PAICE settings did not receive the sign-in. Close this window and select Connect again.'
        },

        teardown() {
            clearTimeout(this.ackTimer)
            if (this.channel) {
                this.channel.close()
                this.channel = null
            }
        },

        postToOpener(payload) {
            const opener = window.opener
            if (!opener || opener.closed) return false
            try {
                // Targeted at our own origin, not '*': the payload carries an
                // authorization code, and a wildcard target would expose it to
                // whatever the opener has since navigated to.
                opener.postMessage(payload, window.location.origin)
                return true
            } catch (e) {
                // Severed or navigated away; the channel is the primary path.
                return false
            }
        },

        closeSelf() {
            try {
                window.close()
            } catch (e) {
                this.message = 'You can close this window now.'
            }
        },
    },
}
</script>

<style scoped>
.oauth-callback {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 2rem;
    text-align: center;
}

.oauth-callback__panel {
    max-width: 26rem;
}

.oauth-callback__title {
    font-size: 1.125rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.oauth-callback__body {
    font-size: 0.875rem;
    opacity: 0.8;
    margin-bottom: 1rem;
}
</style>
